import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@src/reducers/store";
import { setDefaultDashboard } from "@src/reducers/dashboards-slice";
import { patchDashboardV2UseCase } from "@src/cloud-connect/dashboards-v2/use-cases/patch-dashboard-v2-use-case";
import {
  GetDashboardsV2Result,
} from "@src/cloud-connect/dashboards-v2/use-cases/get-dashboards-v2-use-case";

// Reactive effect: if the BE has exactly one PUBLISHED dashboard
// and no default on the BE, PATCH that dashboard to be default.
//
// Editor-only: the viewer page (`/dashboard`) never imports
// this hook, so the read-only contract on the viewer is
// structural.
//
// Read-then-write ordering. We accept the same `listPromise`
// the shared `useDashboards` hook fires and `await` it. No
// second GET is issued: the response inside the promise is the
// fresh BE state that the auto-promote decision reads from.
// Without this wash: a slice-state-driven effect could fire on
// stale Redux data (the slice is app-level; a prior-session
// mount may have left it out of sync with the BE). Awaiting
// the same shared promise guarantees read-before-write by
// construction.
//
// Effect deps. The promise is keyed on auth-context in the
// shared hook, so we fire once per login / re-login and never
// on user-driven dispatches (POST, delete, rename). The user
// described auto-promote as "too random" if triggered by every
// editor action; we agree. Newly-created dashboards can be
// promoted manually via "Set as default" in the editor.
//
// Remote BE changes (another admin, another tab) are out of
// scope for now: SSE-driven refetch will close the loop. Until
// then, the worst case is a stale PATCH based on this hook's
// one-time-at-login GET, which the BE resyncs on the next
// reload-style list read.
//
// Failure path: PATCH failures log and we do NOT revert the
// optimistic dispatch (`setDefaultDashboard`). The BE is the
// source of truth on the next reload.
export const useAutoPromoteDefault = (
  listPromise: Promise<GetDashboardsV2Result> | null,
): void => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!listPromise) return;
    let cancelled = false;
    (async () => {
      const result = await listPromise;
      if (cancelled) return;
      if (!result.success || !result.data) return;

      const list = result.data;
      if (list.length !== 1) return;
      const sole = list[0];
      if (sole.status !== "published") return;
      if (sole.is_default) return;

      dispatch(setDefaultDashboard(sole.id));
      const patchResult = await patchDashboardV2UseCase(sole.id, {
        is_default: true,
      });
      if (cancelled) return;
      if (!patchResult.success) {
        // eslint-disable-next-line no-console
        console.error(
          "Failed to persist auto-promoted default dashboard:",
          patchResult.error,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listPromise, dispatch]);
};
